import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import appStore from "../assets/appStore.png";
import googlePlay from "../assets/google play.png";
import mouSolutions from "../assets/mouLogo.png";
import mouBlanco from "../assets/mouBlanco.png";
import { useEmpresasLanguageOptional } from "../context/EmpresasLanguageContext";

const linkClass = "hover:text-blue-600 transition-colors";

function FooterItem({ item, className = "" }) {
    if (item.path) {
        return (
            <Link to={item.path} className={`${linkClass} ${className}`.trim()}>
                {item.label}
            </Link>
        );
    }
    if (item.href) {
        return (
            <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} ${className}`.trim()}
            >
                {item.label}
            </a>
        );
    }
    return <span className={className}>{item.label}</span>;
}

const defaultSections = [
    {
        id: "interes",
        title: "Te puede interesar",
        items: [
            { label: "¿Quiénes somos?", path: "/quienes-somos" },
            { label: "Trabaja con nosotros", path: "/trabaja-con-nosotros" },
            { label: "Centro de ayuda", path: "/centro-de-ayuda" },
        ],
    },
    {
        id: "legal",
        title: "Legalidades",
        items: [
            { label: "Gobierno corporativo", path: "/gobierno-corporativo" },
            { label: "SARLAFT", path: "/sarlaft" },
            { label: "Protección de datos", path: "/politica-tratamiento-datos" },
            { label: "Términos y condiciones de uso", path: "/terminos-y-condiciones" },
            { label: "Tarifas", path: "/tarifas" },
            { label: "Autorización para el tratamiento de datos personales", path: "/autorizacion-tratamiento-datos" },
            { label: "Política de Resarcimiento", path: "/politica-de-resarcimiento" },
        ],
    },
    {
        id: "consumidor",
        title: "Consumidor",
        items: [
            { label: "Transparencia y acceso a la información", path: "/transparencia" },
            { label: "Atención al consumidor financiero", path: "/atencion-consumidor-financiero" },
            { label: "Defensor del consumidor financiero", path: "/defensor-consumidor-financiero" },
        ],
    },
    {
        id: "contacto",
        title: "Contáctanos",
        items: [
            { label: "Carrera X" },
            { label: "Bogotá +57 322 908 0554" },
        ],
    },
    {
        id: "social",
        title: "Encuéntranos en",
        items: [
            {
                label: "Instagram",
                href: "https://www.instagram.com/meu.aliado?igsh=MTRlbDdpaG1rMDkwcg%3D%3D&utm_source=qr",
            },
            {
                label: "Facebook",
                href: "https://www.facebook.com/share/1BGeSboQxU/?mibextid=wwXIfr",
            },
            {
                label: "LinkedIn",
                href: "https://www.linkedin.com/company/meu-aliado-co/",
            },
        ],
    },
];

const Footer = ({ tipo = "personas" }) => {
    const { t } = useEmpresasLanguageOptional();
    const bgColor = tipo === "empresas" ? "bg-[#E9E6F7]" : "bg-[#E8F2F4]";
    const textColor = tipo === "empresas" ? "text-[#5D00C5]" : "text-[#0054c9]";
    const [openSection, setOpenSection] = useState(null);

    const footerChrome = tipo === "empresas" ? t.chrome.footer : null;
    const sections = useMemo(
        () => (footerChrome ? footerChrome.sections : defaultSections),
        [footerChrome]
    );
    const appTitle = footerChrome?.appTitle ?? "Obtén nuestra App";
    const appTagline =
        footerChrome?.appTagline ??
        "Centralizador de información financiera y corredor de FIC, CDT y pensiones.";
    const copyright =
        footerChrome?.copyright ??
        "© 2025 Todos los derechos reservados | MOU SOLUTIONS S.A.S. 901935902 -1";

    return (
        <div className={`${bgColor} ${textColor} pt-10`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10 text-sm font-bold">
                <div className="lg:hidden space-y-4">
                    {sections.map((section) => {
                        const isOpen = openSection === section.id;
                        return (
                            <div key={section.id} className="border border-slate-200 rounded-2xl bg-white">
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between px-4 py-3 text-left text-blue-900 font-bold"
                                    onClick={() => setOpenSection((prev) => (prev === section.id ? null : section.id))}
                                >
                                    {section.title}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                                {isOpen && (
                                    <div className="px-4 pb-4">
                                        <ul className="space-y-2">
                                            {section.items.map((item) => (
                                                <li key={item.label}>
                                                    <FooterItem item={item} className="block" />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="hidden lg:grid lg:grid-cols-5 lg:col-span-5 gap-10">
                    {sections.map((section) => (
                        <div key={section.id} className="text-left">
                            <h3 className="font-bold mb-5 text-blue-900">{section.title}</h3>
                            <ul className="space-y-3">
                                {section.items.map((item) => (
                                    <li key={item.label}>
                                        <FooterItem item={item} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col items-center text-center lg:col-span-1">
                    <h3 className="font-bold text-blue-900 mb-3">{appTitle}</h3>
                    <img src={mouBlanco} alt="Meu" className="h-32 my-5 drop-shadow-2xl border border-gray-300 rounded-3xl" />
                    <div className="flex gap-2 mt-2">
                        <img src={appStore} alt="App Store" className="w-24 h-auto object-contain" />
                        <img src={googlePlay} alt="Google Play" className="w-24 h-auto object-contain" />
                    </div>
                    <p className="text-center text-xs mt-2">{appTagline}</p>

                </div>
            </div>
            <div className="flex flex-col items-center mt-10 px-4 text-center">
                <img src={mouSolutions} alt="Meu Aliado" className="h-16 mb-3" />
                <p className="text-xs font-bold">{copyright}</p>
            </div>
        </div>
    );
};

export default Footer;
